import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Input,
  Button,
  ModalHeader,
  ModalContent,
  ModalFooter,
  ModalBody,
} from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import {
  MagnifyingGlass,
  ChatTeardropText,
  ArrowLeft,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import {
  getTemplateBody,
  extractTemplateParams,
  replaceTemplateParams,
} from "../util/template-util";

interface WhatsAppTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (
    template: any,
    params: Record<string, Record<string, string>>,
  ) => void;
  participants?: any[];
}

const prettifyName = (raw: string): string => {
  if (!raw) return "Onbekend";
  return raw
    .replace(/_template$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const detectLanguageCodes = (template: any): string[] => {
  if (!template) return [];
  const langs = new Set<string>();
  if (template.language) langs.add(String(template.language));
  if (template.types) {
    Object.values(template.types).forEach((t: any) => {
      if (t?.language) langs.add(String(t.language));
    });
  }
  return Array.from(langs);
};

const renderBodyWithChips = (body: string): React.ReactNode => {
  if (!body) return null;
  const parts = body.split(/(\{\{\d+\}\})/g);
  return parts.map((part, idx) => {
    const match = part.match(/^\{\{(\d+)\}\}$/);
    if (match) {
      return (
        <span
          key={idx}
          className="bg-moss-primary/10 text-moss-primary mx-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium"
        >
          variabele {match[1]}
        </span>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

const paramLabel = (param: string): string => {
  if (param === "1") return "Voornaam";
  if (param === "2") return "Bedrag / detail";
  if (param === "3") return "Datum";
  return `Parameter ${param}`;
};

const safeParse = (raw: any) => {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw || {};
  } catch {
    return {};
  }
};

const WhatsAppTemplateModal: React.FC<WhatsAppTemplateModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  participants,
}) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateParams, setTemplateParams] = useState<
    Record<string, Record<string, string>>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"select" | "configure">("select");
  const { session } = useSession();

  useEffect(() => {
    if (!isOpen) return;
    fetchTemplates();
    // Reset state on each open so it doesn't carry over from a previous run.
    setStep("select");
    setSelectedTemplate(null);
    setSearchQuery("");
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTemplate || !participants) return;
    const body = getTemplateBody(selectedTemplate) || "";
    const params = extractTemplateParams(body);
    const initial: Record<string, Record<string, string>> = {};
    participants.forEach((participant) => {
      const attrs = participant?.attributes
        ? safeParse(participant.attributes)
        : {};
      const fullName: string = attrs.fullName || participant?.identity || "";
      const firstName = attrs.firstName || fullName.split(" ")[0] || "";
      initial[participant.sid] = {};
      params.forEach((param) => {
        if (param === "1") {
          initial[participant.sid][param] = firstName;
        } else {
          initial[participant.sid][param] = "";
        }
      });
    });
    setTemplateParams(initial);
  }, [selectedTemplate, participants]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/whatsapp/templates`,
        { method: "GET" },
        session,
      );
      if (!response.ok) throw new Error("Failed to fetch templates");
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return templates;
    return templates.filter((t) => {
      const name = (t.friendly_name || "").toLowerCase();
      const body = (getTemplateBody(t) || "").toLowerCase();
      return name.includes(q) || body.includes(q);
    });
  }, [templates, searchQuery]);

  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
    setStep("configure");
  };

  const handleSend = () => {
    if (!selectedTemplate) return;
    onSelectTemplate(selectedTemplate, templateParams);
    onClose();
  };

  const handleParamChange = (
    participantSid: string,
    param: string,
    value: string,
  ) => {
    setTemplateParams((prev) => ({
      ...prev,
      [participantSid]: { ...prev[participantSid], [param]: value },
    }));
  };

  const selectedBody = selectedTemplate ? getTemplateBody(selectedTemplate) : "";
  const selectedParams = selectedBody ? extractTemplateParams(selectedBody) : [];
  const selectedLangs = selectedTemplate ? detectLanguageCodes(selectedTemplate) : [];

  const allParamsFilled = useMemo(() => {
    if (!selectedTemplate || !participants || participants.length === 0)
      return true;
    return participants.every((p) => {
      const vals = templateParams[p.sid] || {};
      return selectedParams.every(
        (param) => vals[param] !== undefined && vals[param].trim() !== "",
      );
    });
  }, [selectedTemplate, participants, templateParams, selectedParams]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {step === "select" ? (
            <>
              <h3 className="text-lg font-semibold">Selecteer WhatsApp Template</h3>
              <p className="text-neutral-secondary-mid text-xs font-normal">
                Enkel goedgekeurde Twilio-templates worden getoond.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("select")}
                className="text-neutral-secondary-mid hover:text-neutral-primary inline-flex items-center gap-1 text-xs"
              >
                <ArrowLeft size={14} />
                Andere kiezen
              </button>
              <h3 className="text-lg font-semibold">
                {prettifyName(selectedTemplate?.friendly_name)}
              </h3>
              <div className="flex items-center gap-1">
                {selectedLangs.slice(0, 3).map((l) => (
                  <span
                    key={l}
                    className="bg-neutral-100 text-neutral-secondary-mid rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                  >
                    {l.split("_")[0]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </ModalHeader>

        <ModalBody>
          {step === "select" && (
            <>
              <Input
                placeholder="Zoek op naam of inhoud..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startContent={
                  <MagnifyingGlass className="text-default-400" size={16} />
                }
                classNames={{ inputWrapper: "rounded-full" }}
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {isLoading && (
                  <>
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <div
                        key={`tpl-skel-${idx}`}
                        className="border-neutral-primary/10 space-y-2 rounded-lg border p-4"
                      >
                        <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-200" />
                        <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-200" />
                        <div className="h-3 w-1/3 animate-pulse rounded bg-neutral-200" />
                      </div>
                    ))}
                  </>
                )}

                {!isLoading && filteredTemplates.length === 0 && (
                  <div className="text-neutral-secondary-mid col-span-full py-8 text-center text-sm">
                    Geen goedgekeurde templates gevonden
                    {searchQuery ? ` voor "${searchQuery}"` : ""}.
                  </div>
                )}

                {!isLoading &&
                  filteredTemplates.map((template) => {
                    const body = getTemplateBody(template) || "";
                    const params = extractTemplateParams(body);
                    const langs = detectLanguageCodes(template);
                    return (
                      <button
                        key={template.sid}
                        type="button"
                        onClick={() => handleSelectTemplate(template)}
                        className="border-neutral-primary/10 hover:border-moss-primary/40 hover:bg-neutral-50 relative flex h-full flex-col gap-2 rounded-lg border p-4 text-left transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <ChatTeardropText
                              size={16}
                              weight="duotone"
                              className="text-moss-primary flex-shrink-0"
                            />
                            <span
                              className="text-neutral-primary truncate text-sm font-semibold"
                              title={template.friendly_name}
                            >
                              {prettifyName(template.friendly_name)}
                            </span>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-1">
                            {langs.slice(0, 3).map((l) => (
                              <span
                                key={l}
                                className="bg-neutral-100 text-neutral-secondary-mid rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                              >
                                {l.split("_")[0]}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-neutral-secondary-mid line-clamp-3 text-xs leading-relaxed whitespace-pre-line">
                          {body ? (
                            renderBodyWithChips(body)
                          ) : (
                            <span className="italic">Geen voorbeeldtekst</span>
                          )}
                        </div>
                        <div className="text-neutral-secondary-mid flex items-center gap-2 text-[11px]">
                          <span className="font-mono">
                            {template.friendly_name}
                          </span>
                          {params.length > 0 && (
                            <span className="bg-brick-primary/10 text-brick-primary rounded px-1.5 py-0.5 font-medium">
                              {params.length}{" "}
                              {params.length === 1 ? "variabele" : "variabelen"}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </>
          )}

          {step === "configure" && selectedTemplate && (
            <div className="space-y-4">
              {/* Full body with chips, no truncation */}
              <div className="border-neutral-primary/10 rounded-lg border bg-neutral-50 p-4">
                <div className="text-neutral-secondary-mid mb-2 text-xs font-medium uppercase tracking-wide">
                  Templateinhoud
                </div>
                <div className="text-neutral-primary text-sm leading-relaxed whitespace-pre-line">
                  {selectedBody ? (
                    renderBodyWithChips(selectedBody)
                  ) : (
                    <span className="italic">Geen inhoud</span>
                  )}
                </div>
                <div className="text-neutral-secondary-mid mt-3 flex items-center gap-2 text-[11px]">
                  <span className="font-mono">
                    {selectedTemplate.friendly_name}
                  </span>
                  {selectedParams.length > 0 && (
                    <span className="bg-brick-primary/10 text-brick-primary rounded px-1.5 py-0.5 font-medium">
                      {selectedParams.length}{" "}
                      {selectedParams.length === 1 ? "variabele" : "variabelen"}
                    </span>
                  )}
                </div>
              </div>

              {/* Per-participant fill + live preview */}
              {participants && participants.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-neutral-primary flex items-center gap-2 text-sm font-semibold">
                    <ChatTeardropText
                      size={16}
                      weight="duotone"
                      className="text-moss-primary"
                    />
                    Variabelen invullen &amp; voorbeeld
                  </div>
                  {participants.map((participant) => {
                    const attrs = participant?.attributes
                      ? safeParse(participant.attributes)
                      : {};
                    const fullName: string =
                      attrs.fullName || participant?.identity || "Deelnemer";
                    const previewBody = replaceTemplateParams(
                      selectedBody || "",
                      templateParams[participant.sid] || {},
                    );
                    return (
                      <div
                        key={participant.sid}
                        className="border-neutral-primary/10 rounded-md border bg-white p-3"
                      >
                        <div className="text-neutral-secondary-mid mb-2 text-xs font-medium">
                          Voor {fullName}
                        </div>
                        {selectedParams.length > 0 && (
                          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {selectedParams.map((param) => (
                              <Input
                                key={`${participant.sid}-${param}`}
                                size="sm"
                                label={paramLabel(param)}
                                value={
                                  templateParams[participant.sid]?.[param] || ""
                                }
                                onChange={(e) =>
                                  handleParamChange(
                                    participant.sid,
                                    param,
                                    e.target.value,
                                  )
                                }
                                placeholder={`{{${param}}}`}
                                classNames={{ inputWrapper: "rounded-md" }}
                              />
                            ))}
                          </div>
                        )}
                        <div className="text-neutral-secondary-mid mb-1 text-[11px] uppercase tracking-wide">
                          Voorbeeld
                        </div>
                        <div
                          className="text-neutral-primary rounded-md p-3 text-sm whitespace-pre-line"
                          style={{ backgroundColor: "#d9fdd3" }}
                        >
                          {previewBody}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-neutral-secondary-mid border-neutral-primary/10 rounded-md border bg-white p-3 text-xs">
                  Geen deelnemers gevonden — het bericht wordt verzonden zonder
                  variabelen.
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          {step === "select" ? (
            <Button variant="light" onPress={onClose}>
              Annuleren
            </Button>
          ) : (
            <>
              <Button
                variant="light"
                onPress={() => setStep("select")}
                startContent={<ArrowLeft size={14} />}
              >
                Andere kiezen
              </Button>
              <Button
                className="bg-moss-primary text-white"
                onPress={handleSend}
                isDisabled={!allParamsFilled}
                startContent={<PaperPlaneTilt size={14} />}
              >
                Verzenden
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WhatsAppTemplateModal;
